import * as enabledApi from '@shared/api/enabledPlayApi';
import { StopAllOutputsCommand } from '@shared/constants/CommandValues';
import { isLicensedProfile, isLicenseValidForProfile } from '@shared/constants/profile/ProfileUtils';
import { DEVICE_CURRENT_PROFILE } from '@shared/constants/StorageValues';
import IUserAccess from '@shared/models/components/IUserAccess';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import UserDeviceModel from '@shared/models/devices/UserDeviceModel';
import InvalidResult from '@shared/models/result/InvalidResult';
import IResult from '@shared/models/result/IResult';
import SuccessResult from '@shared/models/result/SuccessResult';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { createContainer } from 'unstated-next';
import { publish, subscribe } from '../helpers/EventCallback';
import useStateRef from '../useStateRef';

/**
 * Container managing the device based on routing of /deviceId
 * In the panel window, CurrentDeviceContainer.id is the same as deviceContainer.outputDeviceId because panel is always showing the current device
 */
function useCurrentDeviceContainer() {
    const userLicenseAccessRef = useRef<IUserAccess | undefined>(undefined);
    const [device, setDevice, deviceRef] = useStateRef<UserDeviceModel | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [previousProfileId, setPreviousProfileId] = useState<string | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [id, setId, idRef] = useStateRef<string | undefined>(undefined);
    const [currentProfile, setCurrentProfile, currentProfileRef] = useStateRef<UserDeviceProfileModel | null>(null);
    const loadInstalledAppNamesRef = useRef<() => void>(() => {});
    const [installedAppNames, setInstalledAppNames] = useState([] as string[]);
    const [tryCommandHandlers, setTryCommandHandlers] = useState<
        ((command: string, profile: UserDeviceProfileModel) => boolean)[]
    >([]);
    const appTrackPrevious = useRef<string>('Cephable');
    const appTrackingCurrentReal = useRef<string>('Cephable');
    const [appTrackingCurrent, setAppTrackingCurrent, appTrackingCurrentRef] = useStateRef<string>('Cephable');

    const [phiNativeResult, setPhiNativeResult, phiNativeResultRef] = useStateRef<string>('');
    const [phiNativeStatus, setPhiNativeStatus, phiNativeStatusRef] = useStateRef<string>('');
    const deviceTypeIdRef = useRef<string | undefined>(undefined);

    const updatePhiResult = (token: string) => {
        setPhiNativeResult(phiNativeResultRef.current + token);
    };
    const resetPhiResult = () => {
        setPhiNativeResult('');
    };

    const addTryCommandHandler = (handler: (command: string, profile: UserDeviceProfileModel) => boolean) => {
        setTryCommandHandlers([...tryCommandHandlers, handler]);
    };

    const updateLocalState = (device: UserDeviceModel | undefined, newProfile: UserDeviceProfileModel | null) => {
        setDevice(device);
        if (idRef.current !== device?.id) {
            setId(device?.id);
            deviceTypeIdRef.current = device?.device?.deviceType.id;
        }

        const existingProfileStr = JSON.stringify(currentProfileRef.current);
        const newProfileStr = JSON.stringify(newProfile);

        let updatedProfile = currentProfileRef.current;
        if (existingProfileStr !== newProfileStr) {
            // license check
            if (newProfile && isLicenseValidForProfile(newProfile, userLicenseAccessRef.current)) {
                updatedProfile = newProfile;
            } else {
                updatedProfile = null;
            }

            // update current profile
            setCurrentProfile(updatedProfile);
            snapshotState(updatedProfile);
        }
        publish('currentDeviceUpdated', { device, profile: updatedProfile });
        return true;
    };

    const snapshotState = (profile: UserDeviceProfileModel | null) => {
        localStorage.setItem(DEVICE_CURRENT_PROFILE, JSON.stringify(profile));
    };

    const updateDeviceState = (device: UserDeviceModel | undefined, newProfile: UserDeviceProfileModel | null) => {
        // updating because device has updated

        let isDeviceEqual = false;
        if (device && deviceRef.current) {
            // sparse property check for current device
            const isIdEqual = device.id === deviceRef.current.id;
            const isModifiedDateEqual = device.modifiedDate === deviceRef.current.modifiedDate;
            isDeviceEqual = isIdEqual && isModifiedDateEqual;
        }

        const isProfileEqual = _.isEqual(newProfile, currentProfileRef.current);
        if (isDeviceEqual && isProfileEqual) {
            // no changes
            return false;
        }

        return updateLocalState(device, newProfile);
    };

    const loadInstalledAppNames = () => {
        loadInstalledAppNamesRef.current();
    };

    const setLoadInstalledAppNamesRef = (handler: () => void) => {
        loadInstalledAppNamesRef.current = handler;
    };

    const tryDeviceCommand = async (command: string, additionalInputContent?: string) => {
        if (currentProfileRef.current && idRef.current && deviceRef.current) {
            for (let handler of tryCommandHandlers) {
                if (handler(command, currentProfileRef.current)) {
                    return true;
                }
            }
        }
        return false;
    };

    const stopAllOutputs = async () => {
        return await tryDeviceCommand(StopAllOutputsCommand);
    };

    const resetCurrentProfile = async () => {
        if (!idRef.current || !deviceRef.current) {
            return new InvalidResult<UserDeviceModel>('Device ID is not set');
        }
        const currentDeviceProfile = currentProfileRef.current;
        let result: IResult<UserDeviceModel>;
        // reset current profile to null
        if (currentDeviceProfile?.isPublic) {
            result = await enabledApi.updateDeviceProfile(idRef.current, {
                defaultProfileId: null,
            });
        } else {
            result = await enabledApi.updateDeviceProfile(idRef.current, {
                customProfileId: null,
            });
        }

        if (result.resultType === 'Ok' && result.data) {
            updateLocalState(result.data, null);
        } else if (result.resultType !== 'Ok') {
            // offline reset
            const updatedDeviceState = _.cloneDeep(deviceRef.current);
            if (currentDeviceProfile?.isPublic) {
                updatedDeviceState.defaultProfileId = null;
            } else {
                updatedDeviceState.currentProfile = null;
            }
            updateLocalState(updatedDeviceState, null);
            result = new SuccessResult<UserDeviceModel>(updatedDeviceState);
        }

        return result;
    };

    useEffect(() => {
        const unsubscribeUserLicenseChange = subscribe('userLicenseChangeLocal', async (data) => {
            if (data) {
                userLicenseAccessRef.current = data;
                if (data.type === 'personal' && isLicensedProfile(currentProfileRef.current)) {
                    // profile requires license and user has expired license
                    await resetCurrentProfile();
                }
            }
        });

        const unsubscribeProfileDeleted = subscribe('profileDeleted', async (data) => {
            if (data && data.profile?.id === currentProfileRef.current?.id) {
                // profile deleted
                await resetCurrentProfile();
            }
        });

        return () => {
            unsubscribeUserLicenseChange();
            unsubscribeProfileDeleted();
            clearAll();
        };
    }, []);

    const clearListeners = () => {
        setTryCommandHandlers([]);
    };

    const clearAll = () => {
        setDevice(undefined);
        setCurrentProfile(null);
        setPreviousProfileId(null);
        setErrors([]);
        setId(undefined);
        setIsLoading(false);
        setInstalledAppNames([]);
        clearListeners();
        localStorage.removeItem(DEVICE_CURRENT_PROFILE);
    };

    return {
        phiNativeResult,
        setPhiNativeResult,
        phiNativeResultRef,
        phiNativeStatus,
        setPhiNativeStatus,
        phiNativeStatusRef,
        updatePhiResult,
        resetPhiResult,
        idRef,
        id,
        isLoading,
        previousProfileId,
        setPreviousProfileId,
        errors,
        userLicenseAccessRef,
        updateDeviceState,
        currentProfile,
        currentProfileRef,
        appTrackPrevious,
        appTrackingCurrentReal,
        appTrackingCurrent,
        setAppTrackingCurrent,
        appTrackingCurrentRef,
        device,
        deviceRef,
        deviceTypeIdRef,
        setLoadInstalledAppNamesRef,
        loadInstalledAppNames,
        installedAppNames,
        setInstalledAppNames,
        addTryCommandHandler,
        tryDeviceCommand,
        stopAllOutputs,
        clearListeners,
        clearAll,
        setIsLoading,
    };
}

const CurrentDeviceContainer = createContainer(useCurrentDeviceContainer);
export default CurrentDeviceContainer;
